import { Directive, OnDestroy, OnInit } from '@angular/core';
import {
  ToolBar,
  IColumnArray,
  BaseFpxRoGridHandleAction,
  SearchType,
  ColumnType,
  SortType,
  BaseFpxDataService,
  SortSearch,
  GridTransformFn,
} from '@fpx/common/models';

@Directive()
export class BaseFpxROGridComponent<T, Helper extends BaseFpxRoGridHelper>
  implements OnInit, OnDestroy
{
  _gridWidth!:number;
  private _gridHeaders: string[] = [];
  private _columnWidths: number[] = [];
  private _columnSortSearch: Map<string, SortSearch> = new Map();
  private _isSortSearch: Map<
    string,
    'sort' | 'search' | 'sort&search' | undefined
  > = new Map();
  private _gridIdentifiers: string[] = [];
  private _gridCell: Map<string, GridTransformFn<any>> = new Map();
  private _count: number = 10;
  private _offset: number = 1;
  gridService!: BaseFpxDataService<any>;
  toolBar: ToolBar[] = [];
  gridData: T[] = [];
  selectedRow: T | undefined;
  private _helper: Helper;
  title: string = '';
  _columnType: ColumnType[] = [];
  transformMap: Map<String,GridTransformFn<any>> = new Map();

  columnsArray: IColumnArray<T>[] = [];

  constructor(_helper: Helper) {
    this._helper = _helper;
  }

  addSortSearch(
    columnName: string,
    isSearchable: boolean,
    isSortable: boolean
  ): void {
    let action: 'sort' | 'search' | 'sort&search' | undefined = undefined;
    if (isSearchable && isSortable) {
      action = 'sort&search';
    } else if (isSearchable) {
      action = 'search';
    } else if (isSortable) {
      action = 'sort';
    }

    this._isSortSearch.set(columnName, action);
  }

  setGridWidth(gridWidth:number) {
    this._gridWidth=gridWidth;
  }

  setGridColumnTypes(columnType: ColumnType[]) {
    this._columnType = columnType;
  }

  setGridHeaders(headerNames: string[]): void {
    this._gridHeaders = headerNames;
  }
  setColumnWidths(columnWidths: number[]): void {
    this._columnWidths = columnWidths;
  }
  setGridIdentifiers(identifiers: string[]): void {
    this._gridIdentifiers = identifiers;
  }
  setGridCell(key: string, value: (element: T) => string): void {
    this._gridCell.set(key, value);
  }
  addGridDataService(gridDataServiceFn: BaseFpxDataService<any>): void {
    this.gridService = gridDataServiceFn;
  }
  addTransformation(column:string,transformFn:GridTransformFn<T>):void 
  {
    // this.transformMap.set(column,transformFn);
    this._gridCell.set(column,transformFn)
  }

  addToolBar(
    type: 'icon' | 'button',
    key: string,
    name: string,
    hoverText = ''
  ): void {
    this.toolBar.push({ type, key, name, hoverText });
  }

  setGridTitle(title: string): void {
    this.title = title;
  }

  handleActions({ name, data }: { name: string; data: any }) {
    this._helper.notifyToolBarHandleActions(name, data);
  }

  ngOnInit(): void {
    
    this.doPreInit();
    
    this.toolBar=this._helper.getToolBar();
    this._isSortSearch=this._helper.getSortSearch();
    this._gridCell=this._helper.getTransformMap();
    this._gridWidth=this._helper.getGridWidth();

    for (let i = 0; i < this._gridIdentifiers.length; i++) {
      if (this._isSortSearch.has(this._gridIdentifiers[i])) {
        this._columnSortSearch.set(this._gridIdentifiers[i], {
          action: this._isSortSearch.get(this._gridIdentifiers[i]),
          columnType: this._columnType[i],
          columnSortType: 'asc',
          searchType: 'Contains',
          searchText: '',
          fromValue: '',
          toValue: '',
        });
      }
    }
    for (let index = 0; index < this._gridHeaders.length; index++) {
      const columns: IColumnArray<any> = {
        action: this._columnSortSearch.get(this._gridIdentifiers[index])
          ?.action,
        columnDef: this._gridIdentifiers[index] ?? '',
        header: this._gridHeaders[index] ?? '',
        cell: this._gridCell.has(this._gridIdentifiers[index])
          ? this._gridCell.get(this._gridIdentifiers[index])
          : undefined,
        width: this._columnWidths[index] ?? 'auto',
        columnType: this._columnSortSearch.get(this._gridIdentifiers[index])
          ?.columnType,
        searchType: this._columnSortSearch.get(this._gridIdentifiers[index])
          ?.searchType,
        sortType: this._columnSortSearch.get(this._gridIdentifiers[index])
          ?.columnSortType,
        searchText:
          this._columnSortSearch.get(this._gridIdentifiers[index])
            ?.searchText ?? '',
        fromValue: this._columnSortSearch.get(this._gridIdentifiers[index])
          ?.fromValue,
        toValue: this._columnSortSearch.get(this._gridIdentifiers[index])
          ?.toValue,
      };
      this.columnsArray.push(columns);
    }
  }

  ngOnDestroy(): void {
    this.doDestroy();
  }

  protected doPreInit(): void {}
  protected doPostInit(): void {}
  protected doDestroy(): void {}
}

export abstract class BaseFpxRoGridHelper {
  private _toolBarhandleActionsMap: Map<String, BaseFpxRoGridHandleAction> =
    new Map();
  protected addHandleActions(
    name: string,
    toolBarhandleActions: BaseFpxRoGridHandleAction
  ): void {
    this._toolBarhandleActionsMap.set(name, toolBarhandleActions);
  }

  public notifyToolBarHandleActions(name: string, data: any) {
    if (this._toolBarhandleActionsMap.has(name)) {
      (this._toolBarhandleActionsMap as Map<string, any>).get(name)(name, data);
    }
  }

  public abstract getToolBar():ToolBar[];
  public abstract getSortSearch(): Map<string,'sort' | 'search' | 'sort&search' | undefined>;
  public abstract getTransformMap():Map<string,GridTransformFn<any>>;
  public abstract getGridWidth():number;

}
